/**
 * Task Card Component
 *
 * Implements:
 * - CON-006: Task Card Component Contract
 */

import type { Task } from "../lib/usePlanReasoning";
import Avatar from "./Avatar";

interface TaskCardProps {
  task: Task;
  onClick?: () => void;
  isSelected?: boolean;
}

export default function TaskCard({ task, onClick, isSelected }: TaskCardProps) {
  const getCardClasses = () => {
    const base = "card cursor-pointer hover:shadow transition-all";
    const selected = isSelected ? "ring-2 ring-primary ring-offset-2 ring-offset-base-100" : "";
    switch (task.status) {
      case "backlog":
        return `${base} ${selected} bg-base-100 shadow-sm`;
      case "ready":
        return `${base} ${selected} bg-primary/10 border border-primary/20`;
      case "in_progress":
        return `${base} ${selected} bg-secondary/10 border border-secondary/20`;
      case "blocked":
        return `${base} ${selected} bg-warning/10 border border-warning/30`;
      case "done":
        return `${base} ${selected} bg-success/5 opacity-60`;
      default:
        return `${base} ${selected} bg-base-100`;
    }
  };

  const getBadgeClass = () => {
    switch (task.agentType) {
      case "C":
        return "badge badge-secondary badge-xs";
      case "S":
        return "badge badge-accent badge-xs";
      case "A":
        return "badge badge-primary badge-xs";
      default:
        return "badge badge-ghost badge-xs";
    }
  };

  const getProgressClass = () => {
    switch (task.status) {
      case "in_progress":
        return task.agentType === "S" ? "progress-accent" : "progress-secondary";
      default:
        return "progress-primary";
    }
  };

  return (
    <div
      className={getCardClasses()}
      onClick={onClick}
      role="button"
      tabIndex={0}
      aria-label={`Task ${task.id}, status: ${task.status}`}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.();
        }
      }}
    >
      <div className="card-body p-2 sm:p-3">
        <div className="flex justify-between items-start">
          <span className={`font-medium text-sm ${task.status === "done" ? "line-through" : ""}`}>
            {task.id}
          </span>
          {task.agentType && (
            <span className={getBadgeClass()}>{task.agentType}</span>
          )}
        </div>

        {/* Status indicator */}
        {task.conclusion && task.status === "ready" && (
          <div className="text-xs text-success hidden sm:block">+d ready</div>
        )}

        {task.conclusion === "-d" && task.status === "blocked" && (
          <div className="text-xs text-error hidden sm:block">-d defeated</div>
        )}

        {/* Dependencies for backlog items */}
        {task.deps && task.status === "backlog" && (
          <div className="text-xs text-base-content/40 hidden sm:block">
            needs: {task.deps}
          </div>
        )}

        {/* Progress bar for in-progress tasks */}
        {task.progress !== undefined && task.status === "in_progress" && (
          <progress
            className={`progress w-full h-1 mt-1 ${getProgressClass()}`}
            value={task.progress}
            max="100"
          />
        )}

        {/* Agent for in-progress tasks */}
        {task.agent && task.status === "in_progress" && (
          <div className="hidden sm:block mt-1">
            <Avatar name={task.agent} keyHash={task.agentKeyHash} size="xs" />
          </div>
        )}
      </div>
    </div>
  );
}
